import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  BookOpen,
  Trophy,
  Users,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Flame,
  Briefcase,
  Newspaper,
} from 'lucide-react';
import { useFeatureFlag } from '../../lib/feature-flags';
import { useState } from 'react';
import { clsx } from 'clsx';
import { useAuthStore } from '../../stores/auth-store';
import { useGamificationStore } from '../../stores/gamification-store';
import { Avatar, StreakBadge, LevelBadge } from '../ui';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const learnerNavItems: NavItem[] = [
  { path: '/app', label: 'Home', icon: <Home className="w-5 h-5" /> },
  { path: '/app/practice', label: 'Practice', icon: <BookOpen className="w-5 h-5" /> },
  { path: '/app/updates', label: 'Updates', icon: <Newspaper className="w-5 h-5" /> },
  { path: '/app/leaderboard', label: 'Leaderboard', icon: <Trophy className="w-5 h-5" /> },
  { path: '/app/profile', label: 'Profile', icon: <User className="w-5 h-5" /> },
  // 6th+ items render in the desktop nav and the mobile menu, not the 5-slot tab bar
  { path: '/app/community', label: 'Community', icon: <Users className="w-5 h-5" /> },
];

const interviewPrepNavItem: NavItem = {
  path: '/app/interview-prep',
  label: 'Interviews',
  icon: <Briefcase className="w-5 h-5" />,
};

// Helper to get display name for training level
function getTrainingLevelDisplay(user: { training_level?: string; pgy_year?: number } | null): { name: string; icon: string } {
  if (!user?.training_level) return { name: 'Learner', icon: '📚' };

  const levelMap: Record<string, { name: string; icon: string }> = {
    'medical_student': { name: 'Medical Student', icon: '📚' },
    'resident': { name: user.pgy_year ? `PGY-${user.pgy_year} Resident` : 'Resident', icon: '🩺' },
    'fellow': { name: 'Fellow', icon: '🎓' },
    'attending': { name: 'Attending', icon: '👨‍⚕️' },
    'np_pa': { name: 'NP/PA', icon: '💉' },
    'program_evaluator': { name: 'Evaluator', icon: '🏥' },
    'other': { name: 'Healthcare Pro', icon: '🌟' },
  };

  return levelMap[user.training_level] || { name: 'Learner', icon: '📚' };
}

export function Navbar() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuthStore();
  const { currentStreak, level } = useGamificationStore();
  const trainingLevel = getTrainingLevelDisplay(user);
  const interviewFlagOn = useFeatureFlag('core.INTERVIEW_PREP');
  const desktopNavItems = interviewFlagOn
    ? [
        learnerNavItems[0],
        learnerNavItems[1],
        interviewPrepNavItem,
        ...learnerNavItems.slice(2),
      ]
    : learnerNavItems;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <>
      {/* Desktop Navbar */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 h-16 bg-white border-b border-cream-300 z-30">
        <div className="w-full max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/app" className="flex items-center gap-2">
            <span className="text-2xl">💚</span>
            <span className="text-xl font-bold text-gradient">PulseMD</span>
          </Link>

          {/* Navigation Items */}
          <div className="flex items-center gap-1">
            {desktopNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-xl font-medium transition-all duration-200',
                    isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-charcoal-400 hover:bg-cream-100 hover:text-charcoal-600'
                  )}
                >
                  {item.icon}
                  <span className="hidden lg:inline">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Section — shrink-proof: the avatar/menu must never be pushed
              off-screen, so the decorative badges yield first (level badge only
              at xl+, streak at lg+). */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Streak */}
            {currentStreak > 0 && (
              <div className="hidden lg:block">
                <StreakBadge days={currentStreak} />
              </div>
            )}

            {/* Training Level */}
            <div className="hidden xl:block">
              <LevelBadge
                level={level}
                name={trainingLevel.name}
                icon={trainingLevel.icon}
              />
            </div>

            {/* User Menu */}
            <div className="relative group">
              <button className="flex items-center gap-2 p-2 rounded-xl hover:bg-cream-100 transition-colors">
                <Avatar
                  src={user?.avatar_url}
                  name={user?.full_name || 'User'}
                  size="sm"
                />
              </button>

              {/* Dropdown */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-cream-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="p-3 border-b border-cream-200">
                  <p className="font-medium text-charcoal-600 truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-sm text-charcoal-400 truncate">
                    {user?.email}
                  </p>
                </div>
                <div className="p-2">
                  <Link
                    to="/app/settings"
                    className="flex items-center gap-2 px-3 py-2 text-charcoal-500 hover:bg-cream-100 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 w-full px-3 py-2 text-charcoal-500 hover:bg-cream-100 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-cream-300 z-30 safe-bottom">
        <div className="h-full flex items-center justify-around px-2">
          {learnerNavItems.slice(0, 5).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={clsx(
                  'flex flex-col items-center gap-1 px-3 py-2 rounded-xl min-w-touch transition-all duration-200',
                  isActive
                    ? 'text-primary-500'
                    : 'text-charcoal-400'
                )}
              >
                {item.icon}
                <span className="text-xs font-medium">{item.label}</span>
                {isActive && (
                  <motion.div
                    className="absolute bottom-1 w-1 h-1 bg-primary-400 rounded-full"
                    layoutId="activeTab"
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-cream-300 z-30 safe-top">
        <div className="h-full flex items-center justify-between px-4">
          <Link to="/app" className="flex items-center gap-2">
            <span className="text-xl">💚</span>
            <span className="text-lg font-bold text-gradient">PulseMD</span>
          </Link>

          <div className="flex items-center gap-3">
            {currentStreak > 0 && (
              <div className="flex items-center gap-1 text-coral-400">
                <Flame className="w-5 h-5" />
                <span className="font-semibold">{currentStreak}</span>
              </div>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 text-charcoal-500 hover:bg-cream-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="md:hidden fixed inset-0 bg-charcoal-900/50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-cream-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar
                  src={user?.avatar_url}
                  name={user?.full_name || 'User'}
                  size="md"
                />
                <div>
                  <p className="font-medium text-charcoal-600">
                    {user?.full_name}
                  </p>
                  <LevelBadge
                    level={level}
                    name={trainingLevel.name}
                    icon={trainingLevel.icon}
                  />
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-charcoal-400 hover:bg-cream-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              {learnerNavItems.slice(5).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-charcoal-500 hover:bg-cream-100 rounded-xl transition-colors"
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
              <Link
                to="/app/settings"
                className="flex items-center gap-3 px-4 py-3 text-charcoal-500 hover:bg-cream-100 rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>
              <button
                onClick={() => {
                  handleSignOut();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 text-charcoal-500 hover:bg-cream-100 rounded-xl transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default Navbar;
